<template>
  <textarea
    ref="textarea"
    v-model="model"
    :autocomplete="autocomplete"
    :cols="cols"
    :maxlength="maxlength"
    :placeholder="placeholder"
    :readonly="disabled"
    :rows="rows"
    :wrap="wrap"
  />
</template>

<script setup>
import { useTextareaAutosize, watchDebounced } from '@vueuse/core';

/** @import { PropType } from 'vue' */

defineProps({
  /** This attribute indicates whether the value of the control can be automatically completed by the browser. */
  autocomplete: {
    type: String,
    default: null,
    validator: (value) => typeof value === 'string' && ['on', 'off'].includes(value),
  },

  /** Specifies the visible width of a text area. */
  cols: {
    type: [Number, String],
    default: null,
  },

  /** Specifies that a text area should be disabled. */
  disabled: {
    /** @type {PropType<boolean | 'true' | 'false'>} */
    type: [Boolean, String],
    default: false,
  },

  /**
   * The maximum string length (measured in UTF-16 code units) that the user can enter.
   * If this value isn't specified, the user can enter an unlimited number of characters.
   */
  maxlength: {
    type: [Number, String],
    default: null,
  },

  /** Specifies a short hint that describes the expected value of a text area. */
  placeholder: { type: String, default: null },

  /** Specifies the visible number of lines in a text area. */
  rows: {
    type: [Number, String],
    default: null,
  },

  /** Specifies how the text in a text area is to be wrapped when submitted in a form. */
  wrap: {
    /** @type {PropType<'hard' | 'soft' | 'off'>} */
    type: String,
    default: null,
    validator: (value) => typeof value === 'string'
      && ['hard', 'soft', 'off'].includes(value),
  },
});

const model = defineModel({ type: String });

const { textarea, triggerResize } = useTextareaAutosize();

watchDebounced(
  model,
  () => {
    triggerResize();
  },
  { debounce: 300, maxWait: 1000 },
);
</script>

<style scoped lang="scss">
textarea {
  background-color: var(--his-light-blue);
  border-radius: 6px;
  font-size: smaller;
  width: 100%;
  padding: calc(var(--f7-typography-padding) / 2);

  &:focus-visible {
    outline: 1px solid var(--his-oliva);
  }

  &:read-only {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
